import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface StatusDist {
    status: string;
    count: number;
    percentage: number;
}

interface StatusDistributionChartProps {
    data: StatusDist[];
}

const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'];

export const StatusDistributionChart: FC<StatusDistributionChartProps> = ({ data }) => {
    const { t } = useTranslation();

    // Status değerlerini Türkçeleştir
    const getStatusLabel = (status: string): string => {
        const statusMap: Record<string, string> = {
            success: t('aiStudioAnalytics.successful'),
            successful: t('aiStudioAnalytics.successful'),
            error: t('aiStudioAnalytics.failed'),
            failed: t('aiStudioAnalytics.failed'),
            pending: t('general.pending'),
            processing: t('general.processing'),
        };
        return statusMap[status.toLowerCase()] || status;
    };

    // Veri kontrolü ve filtreleme
    if (!data || data.length === 0) {
        return (
            <Card elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: '24px !important' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                        {t('aiStudioAnalytics.statusDistribution')}
                    </Typography>
                    <Box
                        sx={{
                            width: '100%',
                            height: 350,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography color="textSecondary">{t('aiStudioAnalytics.noData')}</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Count değeri 0'dan büyük olanları filtrele ve Türkçeleştirilmiş label ekle
    const formattedData = data
        .filter((item) => item.count > 0)
        .map((item) => ({
            ...item,
            localizedStatus: getStatusLabel(item.status),
        }));

    // Eğer filtrelemeden sonra veri kalmadıysa
    if (formattedData.length === 0) {
        return (
            <Card elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: '24px !important' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                        {t('aiStudioAnalytics.statusDistribution')}
                    </Typography>
                    <Box
                        sx={{
                            width: '100%',
                            height: 350,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography color="textSecondary">{t('aiStudioAnalytics.noData')}</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Custom label renderer - sadece %2'den büyük olanları göster
    const renderLabel = (props: any) => {
        const { cx, cy, midAngle, outerRadius, percentage, localizedStatus } = props;

        if (percentage < 2) {
            return null;
        }

        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 20;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="#475569"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                style={{ fontSize: '0.75rem', fontWeight: 600 }}
            >
                {`${localizedStatus}: ${percentage.toFixed(1)}%`}
            </text>
        );
    };

    // Custom tooltip renderer
    const renderTooltip = (props: any) => {
        const { active, payload } = props;
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Box
                    sx={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: 1.5,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'text.primary' }}>
                        {data.localizedStatus}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('aiStudioAnalytics.count')}: {data.count}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('aiStudioAnalytics.percentage')}: {data.percentage.toFixed(1)}%
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    // Custom legend renderer
    const renderLegend = (props: any) => {
        const { payload } = props;
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, flexWrap: 'wrap', mt: 2 }}>
                {payload.map((entry: any, index: number) => (
                    <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: entry.color,
                                borderRadius: '50%',
                            }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b' }}>
                            {entry.payload.localizedStatus}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Card elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: '24px !important' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                    {t('aiStudioAnalytics.statusDistribution')}
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={formattedData}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={renderLabel}
                                outerRadius={100}
                                innerRadius={60} // Donut chart is more corporate
                                fill="#8884d8"
                                dataKey="count"
                            >
                                {formattedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={renderTooltip} />
                            <Legend content={renderLegend} />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};
